function safeIsPointInRange(range: Range, node: Node, offset: number): boolean {
	try {
		return range.isPointInRange(node, offset);
	} catch {
		// some node types can throw, treat as "outside"
		return false;
	}
}

function fullyInside(r: Range, container: Range): boolean {
	return (
		safeIsPointInRange(container, r.startContainer, r.startOffset) &&
		safeIsPointInRange(container, r.endContainer, r.endOffset)
	);
}

/**
 * Find the "main" ProseMirror for a given sidebar container:
 * Walk up from the sidebar to the nearest .ak-editor-content-area. Return its direct
 * child .ProseMirror (even if other editors exist deeper in the subtree).
 */
function getMainProseMirrorForSidebar(sidebar: Element | string): HTMLElement | null {
	const sidebarEl =
		typeof sidebar === 'string' && typeof document !== 'undefined'
			? document.querySelector<HTMLElement>(sidebar)
			: (sidebar as HTMLElement | null);
	if (!sidebarEl) {
		return null;
	}

	const contentArea = sidebarEl.closest<HTMLElement>('.ak-editor-content-area');
	if (!contentArea) {
		return null;
	}

	for (const child of Array.from(contentArea.children)) {
		if (child instanceof HTMLElement && child.classList.contains('ProseMirror')) {
			return child;
		}
	}
	return null;
}

function countWords(text: string): number {
	const t = text.trim();
	if (!t) {
		return 0;
	}

	try {
		const Segmenter = Intl?.Segmenter;
		if (Segmenter) {
			const seg: any = new Segmenter(undefined, { granularity: 'word' });
			let c = 0;
			for (const { isWordLike } of seg.segment(t)) {
				if (isWordLike) {
					c++;
				}
			}
			return c;
		}
	} catch {}
	// @ts-ignore - TS1501 TypeScript 5.9.2 upgrade
	return t.split(/\s+/u).filter(Boolean).length;
}

interface SelectionCounts {
	chars: number;
	text: string;
	words: number;
}

// Temporary: DOM-based selection metrics adapter.
// Purpose: Bridge until the editor plugin API implementation lands (See: EDITOR-1544)
export function selectionCountsInMainEditor(sidebar: Element | string): SelectionCounts | null {
	try {
		const editor = getMainProseMirrorForSidebar(sidebar);
		if (!editor) {
			return null;
		}

		const doc: Document | null =
			editor.ownerDocument ?? (typeof document !== 'undefined' ? document : null);
		if (!doc) {
			return null;
		}

		const sel = doc.getSelection?.();
		if (!sel || sel.rangeCount === 0 || sel.isCollapsed) {
			return null;
		}

		// range covering just the editor's contents to test containment
		const container = doc.createRange();
		container.selectNodeContents(editor);

		// snapshot rangeCount up-front to avoid races if selection changes
		const rangeCount = sel.rangeCount;

		// require every selection range to be fully inside
		let text = '';
		for (let i = 0; i < rangeCount; i++) {
			const r = sel.getRangeAt(i);
			if (!fullyInside(r, container)) {
				return null;
			}
			const part = r.toString();
			text += text ? `\n${part}` : part;
		}

		return {
			text,
			words: countWords(text),
			chars: text.length,
		};
	} catch (err) {
		return null;
	}
}
